/**
 * Journal mutations with error handling and toasts.
 *
 * No manual invalidation: PowerSync queries re-run themselves when observed
 * tables change (onChangeWithCallback).
 */
import { msg } from "@lingui/core/macro";
import {
  addTagToEntryMutation,
  createEntryMutation,
  createTagMutation,
  deleteEntryMutation,
  deleteEntryPhotoMutation,
  deleteTagMutation,
  removeTagFromEntryMutation,
  restoreEntryMutation,
  restoreEntryVersionMutation,
  setEntryFavoriteMutation,
  trashEntryMutation,
  updateEntryMutation,
} from "@journal/shared";
import type { EntryVersion } from "@journal/powersync";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { useJournalData } from "./use-journal-data";

export interface SaveEntryInput {
  entryDate: string;
  body: string;
  title?: string | null;
  mood?: number | null;
  moodTags?: string[] | null;
  templateKey?: string | null;
  tagIds?: string[];
}

export function useEntryMutations() {
  const { drizzle, userId } = useJournalData();
  const { t } = useI18n();

  const requireDb = () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      throw new Error("Local database not ready");
    }
    return { db, uid };
  };

  const createEntry = async (input: SaveEntryInput): Promise<string | null> => {
    try {
      const { db, uid } = requireDb();
      const entryId = await createEntryMutation({
        db,
        userId: uid,
        entryDate: input.entryDate,
        body: input.body,
        title: input.title,
        mood: input.mood,
        moodTags: input.moodTags,
        templateKey: input.templateKey,
      });
      for (const tagId of input.tagIds ?? []) {
        await addTagToEntryMutation(db, uid, entryId, tagId);
      }
      return entryId;
    } catch (error) {
      console.error("Error creating journal entry:", error);
      showToast(t(msg`Could not save your entry. Please try again.`), "error");
      return null;
    }
  };

  const updateEntry = async (
    entryId: string,
    patch: Omit<SaveEntryInput, "templateKey" | "tagIds">,
  ): Promise<boolean> => {
    try {
      const { db } = requireDb();
      await updateEntryMutation({
        db,
        entryId,
        entryDate: patch.entryDate,
        body: patch.body,
        title: patch.title,
        mood: patch.mood,
        moodTags: patch.moodTags,
      });
      return true;
    } catch (error) {
      console.error("Error updating journal entry:", error);
      showToast(t(msg`Could not save your entry. Please try again.`), "error");
      return false;
    }
  };

  /** L2: delete = move to trash (soft-delete; restorable). */
  const trashEntry = async (entryId: string): Promise<boolean> => {
    try {
      const { db } = requireDb();
      await trashEntryMutation(db, entryId);
      showToast(t(msg`Entry moved to trash.`), "success");
      return true;
    } catch (error) {
      console.error("Error trashing journal entry:", error);
      showToast(t(msg`Could not delete the entry.`), "error");
      return false;
    }
  };

  /** L2: restore from trash. */
  const restoreEntry = async (entryId: string): Promise<boolean> => {
    try {
      const { db } = requireDb();
      await restoreEntryMutation(db, entryId);
      showToast(t(msg`Entry restored.`), "success");
      return true;
    } catch (error) {
      console.error("Error restoring journal entry:", error);
      showToast(t(msg`Could not restore the entry.`), "error");
      return false;
    }
  };

  /** L2: permanent deletion from trash (irreversible). */
  const deleteEntryForever = async (entryId: string): Promise<boolean> => {
    try {
      const { db } = requireDb();
      await deleteEntryMutation(db, entryId);
      showToast(t(msg`Entry permanently deleted.`), "success");
      return true;
    } catch (error) {
      console.error("Error deleting journal entry:", error);
      showToast(t(msg`Could not delete the entry.`), "error");
      return false;
    }
  };

  /**
   * L4: restoring a version = normal forward write (the current state is
   * archived as a new version by the write-path).
   */
  const restoreVersion = async (entryId: string, version: EntryVersion): Promise<boolean> => {
    try {
      const { db } = requireDb();
      await restoreEntryVersionMutation(db, entryId, version);
      showToast(t(msg`Version restored.`), "success");
      return true;
    } catch (error) {
      console.error("Error restoring entry version:", error);
      showToast(t(msg`Could not restore this version.`), "error");
      return false;
    }
  };

  const setFavorite = async (entryId: string, isFavorite: boolean): Promise<void> => {
    try {
      const { db } = requireDb();
      await setEntryFavoriteMutation(db, entryId, isFavorite);
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const createTag = async (name: string): Promise<string | null> => {
    try {
      const { db, uid } = requireDb();
      return await createTagMutation(db, uid, name);
    } catch (error) {
      console.error("Error creating tag:", error);
      showToast(t(msg`Could not create the tag.`), "error");
      return null;
    }
  };

  const deleteTag = async (tagId: string): Promise<void> => {
    try {
      const { db } = requireDb();
      await deleteTagMutation(db, tagId);
    } catch (error) {
      console.error("Error deleting tag:", error);
      showToast(t(msg`Could not delete the tag.`), "error");
    }
  };

  const addTagToEntry = async (entryId: string, tagId: string): Promise<void> => {
    try {
      const { db, uid } = requireDb();
      await addTagToEntryMutation(db, uid, entryId, tagId);
    } catch (error) {
      console.error("Error tagging entry:", error);
    }
  };

  const removeTagFromEntry = async (entryId: string, tagId: string): Promise<void> => {
    try {
      const { db } = requireDb();
      await removeTagFromEntryMutation(db, entryId, tagId);
    } catch (error) {
      console.error("Error untagging entry:", error);
    }
  };

  const deletePhoto = async (photoId: string): Promise<void> => {
    try {
      const { db } = requireDb();
      await deleteEntryPhotoMutation(db, photoId);
    } catch (error) {
      console.error("Error deleting photo:", error);
      showToast(t(msg`Could not delete the photo.`), "error");
    }
  };

  return {
    createEntry,
    updateEntry,
    trashEntry,
    restoreEntry,
    deleteEntryForever,
    restoreVersion,
    setFavorite,
    createTag,
    deleteTag,
    addTagToEntry,
    removeTagFromEntry,
    deletePhoto,
  };
}
